import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Crown, Sparkles, BookOpen, Lock, Layers, History } from "lucide-react";
import Stars from "@/components/Stars";
import StatusBadge from "@/components/StatusBadge";
import { PickMeta } from "@/components/PickCardParts";
import { LegList, PickAnalysisDialog } from "@/components/PickAnalysisDialog";
import { MARKET_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTimezone, fmtEvent, tzAbbr } from "@/lib/time";

const SPORT = { nba: "🏀 NBA", soccer: "⚽ Soccer" };

function EventState({ state }) {
  if (!state || state === "final") return null;
  const map = {
    upcoming: ["Upcoming", "border-[#2A364F] text-slate-300"],
    in_progress: ["In Progress", "border-[#F59E0B]/50 bg-[#F59E0B]/10 text-[#F59E0B]"],
    awaiting_result: ["Awaiting Result", "border-[#2A364F] text-slate-400"],
  };
  const [label, cls] = map[state] || map.upcoming;
  return <span className={`tag border ${cls}`} data-testid={`event-state-${state}`}>{state === "in_progress" && <span className="live-dot h-1.5 w-1.5 rounded-full bg-[#F59E0B]" />}{label}</span>;
}

export function PickCard({ pick, index = 0, onUnlock }) {
  const { isVip } = useAuth();
  const { tz } = useTimezone();
  const [open, setOpen] = useState(false);
  const locked = pick.locked ?? (pick.visibility === "vip" && !isVip);
  const multi = pick.pick_type === "multi";
  const title = multi ? `${pick.legs?.length || 0}-Leg ${pick.league} Combination` : (pick.player || pick.selection);
  const selection = !multi && pick.player ? pick.selection : null;
  const eventIso = pick.event_start_utc || pick.game_time;

  return (
    <motion.article
      data-testid={`pick-card-${pick.id}`}
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="card-mp card-hover flex flex-col p-5 sm:p-6"
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="tag border border-[#2A364F] bg-[#0F131D] text-slate-300">
          {SPORT[pick.sport] || pick.league} · {multi ? "Multi" : (MARKET_LABELS[pick.market] || pick.market)}
        </span>
        {pick.visibility === "free" ? (
          <span className="tag border border-[#00E676]/40 bg-[#00E676]/10 text-[#00E676]">Free</span>
        ) : (
          <span className="tag border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]"><Crown size={10} /> VIP</span>
        )}
        {pick.best_bet && <span className="tag border border-[#00E676]/40 bg-[#00E676]/10 text-[#00E676]"><Sparkles size={10} /> Best Bet</span>}
        {pick.revised_at && <span className="tag border border-[#38BDF8]/40 bg-[#38BDF8]/10 text-[#38BDF8]" title={`Selections revised ${fmtEvent(pick.revised_at, tz)} ${tzAbbr(tz, pick.revised_at)} — the original selections are kept in the audit history`} data-testid={`pick-revised-${pick.id}`}><History size={10} /> Revised {fmtEvent(pick.revised_at, tz)} {tzAbbr(tz, pick.revised_at)}</span>}
        <span className="ml-auto flex items-center gap-2">
          <EventState state={pick.event_state} />
          <StatusBadge status={pick.status === "void" ? "push" : pick.status} profit={pick.profit} />
        </span>
      </div>

      <p className="font-mono text-[11px] uppercase tracking-wider text-slate-500">{pick.game}</p>
      <p className="font-mono text-[11px] text-[#00E676]/80" data-testid={`pick-event-time-${pick.id}`}>{fmtEvent(eventIso, tz)} {tzAbbr(tz, eventIso)}</p>
      <h3 className={`mt-1 font-display text-2xl font-bold uppercase tracking-wide leading-tight ${locked && !multi ? "text-slate-500 blur-[3px] select-none" : "text-white"}`}>
        {multi && <Layers size={18} className="mr-1 inline text-[#00E676]" />}{title}
      </h3>
      {selection && <p className={`mt-0.5 font-display text-lg font-semibold uppercase tracking-wide ${locked ? "text-slate-600 blur-[3px] select-none" : "text-[#00E676]"}`}>{selection}</p>}

      <PickMeta pick={{ ...pick, postedLabel: new Intl.DateTimeFormat("en-GB", { timeZone: tz, day: "numeric", month: "short" }).format(new Date(pick.posted_at)) }} />

      <div className="mt-3 flex items-center gap-2">
        <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Confidence</span>
        <Stars value={pick.confidence} />
      </div>

      <div className="relative mt-4 flex-1 space-y-3">
        {multi && <LegList legs={pick.legs} locked={locked} compact />}
        <p className="text-sm leading-relaxed text-slate-400">{pick.analysis_preview || (locked ? "Full research available to VIP members." : "")}</p>
        {locked ? (
          <button data-testid={`unlock-analysis-btn-${pick.id}`} onClick={onUnlock} className="inline-flex items-center gap-2 rounded-lg border border-[#F59E0B]/50 bg-[#F59E0B]/10 px-4 py-2 font-display text-xs font-bold uppercase tracking-wider text-[#F59E0B] transition-colors hover:bg-[#F59E0B]/20">
            <Lock size={13} /> Unlock Full Analysis
          </button>
        ) : (
          <button data-testid={`read-analysis-btn-${pick.id}`} onClick={() => setOpen(true)} className="inline-flex items-center gap-2 rounded-lg border border-[#00E676]/40 bg-[#00E676]/10 px-4 py-2 font-display text-xs font-bold uppercase tracking-wider text-[#00E676] transition-colors hover:bg-[#00E676]/20">
            <BookOpen size={13} /> Read Full Analysis
          </button>
        )}
      </div>

      {pick.status !== "pending" && (
        <Link to="/results" data-testid={`pick-result-link-${pick.id}`} className="mt-4 font-mono text-[11px] uppercase tracking-wider text-slate-500 transition-colors hover:text-[#00E676]">
          View on results ledger →
        </Link>
      )}
      <PickAnalysisDialog pick={pick} open={open} onOpenChange={setOpen} onUnlock={onUnlock} />
    </motion.article>
  );
}

export default PickCard;
